import { DispensaryTaxTypeEnum } from "@leafly-com/reservations-api";

import { TaxType } from "./Cart";
import { Coordinates } from "./Coordinates";
import { ScheduleStatuses, WeeklySchedule } from "./DispensarySchedules";
import { LocationPaths } from "./LocationPaths";

export type DispensaryPhoto = {
  id: number;
  bigThumb?: string;
  created?: string | Date;
  imageUrl: string;
  lastModified?: string | Date;
  smallThumb?: string;
  user?: string;
};

export type DispensaryServiceAreaRanges = {
  minFee: number;
  freeDeliveryMinimum: number | null;
  maxFee: number;
  minOrderMinimum: number;
  maxOrderMinimum: number;
  minDeliveryDurationMinimum: number;
  maxDeliveryDurationMaximum: number;
  minFreeDeliveryMinimum: number | null;
  maxFreeDeliveryMinimum: number | null;
};

export type DeliveryServiceAreaInfo = {
  fee: number;
  orderMinimum: number;
  deliveryDurationMinimum: number;
  deliveryDurationMaximum: number;
};

export type Dispensary = {
  acceptsCash: boolean;
  acceptsCreditCards: boolean;
  acceptsDebitCards: boolean;
  address1: string;
  address2: string;
  atmosphereRating: number;
  city: string;
  clinicWalkInsWelcome: boolean;
  country: string | null;
  coverPhotoUrl: string;
  coverImage?: string;
  coverImages?: CoverImages;
  currentStatuses: ScheduleStatuses | null;
  created: string;
  deliveryEnabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  deliveryFee: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  deliveryOrderMinimum: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  deliveryPaymentMethods: any;
  deliveryRadiusMiles: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  deliveryRadiusPostalCodeBlacklist: any;
  deliveryServiceAreaRanges?: DispensaryServiceAreaRanges;
  deliveryServiceAreaInfo?: DeliveryServiceAreaInfo;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  deliveryTimeEstimate?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  deliveryZonePostalCodeWhitelist: any;
  deliveryZoneRangeType: string;
  description: string;
  distanceMi?: number;
  email: string;
  featureTier: string;
  flags?: string[];
  followerCount: number;
  hasAgreedToPrivacyTos: boolean;
  hasAtm: boolean;
  hasChatEnabled: boolean;
  hasDeliveryEnabled: boolean;
  hasJaneCheckoutEnabled: boolean;
  hasMenuItems: boolean;
  hasReservationsEnabled: boolean;
  hasVeteranDiscount: boolean;
  headerTags: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  hours?: any;
  id: number;
  inStoreCartEnabled?: boolean;
  inStorePaymentsAccepted: boolean;
  isAd?: boolean;
  isAdaAccessible: boolean;
  isApproved: boolean;
  isPreorderOpen: boolean;
  isPickupOpen: boolean;
  isDeliveryOpen: boolean;
  isNewDispensary: boolean;
  isUfcwMember: boolean;
  lastMenuUpdate: string;
  leaflyListYears: number[];
  licenseNumber: string;
  location?: Coordinates;
  locations: {
    lat: number;
    lon: number;
  }[];
  locationPaths?: LocationPaths;
  logoUrl: string;
  logoImage?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  mapMarkerLocations: any;
  mapMarkerLevel?: string;
  menuSyncOptionId: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  merchandisedCampaigns?: Array<any>;
  mostRecentActiveDeal?: {
    id?: number;
    discountLabel: string;
    isMedical: boolean;
    isRecreational: boolean;
    title: string;
    externalKind?: string | null;
  };
  name: string;
  numberOfReviews?: number;
  offersMedicalCards: boolean;
  offersWalletSizedMedicalCards: boolean;
  onlineFulfillmentEnabled: boolean;
  onlinePaymentsAccepted: boolean;
  orderMedIdCondition: string;
  organizationSlug: string;
  path?: string;
  phone: string;
  photos: Array<DispensaryPhoto>;
  pickupEnabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  pickupPaymentMethods: any;
  pickupMinutesMaximum?: number;
  pickupMinutesMinimum?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  pickupTimeEstimate: any;
  preferredPickupPartner: boolean;
  premiumRank?: PremiumRank;
  preorderConfigured: boolean;
  preorderEnabled: boolean;
  primaryLocation: Coordinates;
  productCategories?: {
    name: string;
  }[];
  qualityRating: number;
  rating: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  recentUpdates?: Array<any>;
  retailType: string;
  reviewCount: number;
  roundedRating: number;
  schedules: WeeklySchedule | null;
  scheduledDeliveryEnabled: boolean;
  slug: string;
  serviceRating: number;
  specialCheckoutInstructions: string | null;
  sponsoredVideoId?: string;
  starRating?: number;
  state: string;
  supportsSmsNotifications: boolean;
  tagLine: string;
  tags: DispensaryTags[];
  taxCalculationEnabled: boolean;
  taxType: TaxType | DispensaryTaxTypeEnum;
  tier?: 0 | 1 | 2 | 3 | 4 | 200 | 300 | 400 | 900;
  timeZone: string;
  topLevelOrganizationSlug?: string;
  verifiesMedicalCards247: boolean;
  website: string;
  zip: string;
  openNow: boolean;
  uberEatsStoreUrl?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  openNext?: any;
  openUntil?: string | Date;
  latitude?: string;
  longitude?: string;
  matchedStrainNames?: {
    name: string;
  }[];
};

export type DeliveryServiceArea = {
  freeDeliveryMinimum: number | null;
  deliveryDurationMaximum: string | number;
  deliveryDurationMinimum: string | number;
  fee: string | number;
  id: number;
  name: string;
  orderMinimum: string | number;
};

type PremiumRank = "diamond" | "platinum" | "gold" | "silver" | "bronze";

type CoverImages = {
  small: string;
  medium: string;
  large: string;
};

// These types used to be imported from API-OG but were extracted
// due to bundle sizing issues. Please consult API-OG code if you
// feel something may be missing.
export type DispensaryTags =
  | "medical"
  | "recreational"
  | "delivery"
  | "canadaLp"
  | "storefront"
  | "azLicensed"
  | "comingSoon"
  | "licensed"
  | "dualLicense"
  | "indigenous";

export enum RetailType {
  Clinic = "clinic",
  Dispensary = "dispensary",
  CbdStore = "cbd-store",
}
